/**
 * useDragAndPaste - Hook para drag & drop e paste de arquivos
 * Gerencia eventos de arrastar e colar arquivos no chat
 */

import { useCallback, useEffect, useState, RefObject } from 'react';
import { toast } from 'sonner';

export interface DragAndPasteFile {
  file: File;
  preview?: string; // Para imagens
  id: string;
}

interface UseDragAndPasteOptions {
  /** Elemento container para eventos de drag/paste */
  targetRef: RefObject<HTMLElement>;
  /** Callback quando arquivos são adicionados */
  onFilesAdded?: (files: DragAndPasteFile[]) => void;
  /** Tipos de arquivo aceitos (MIME types e/ou extensões `.ext`) */
  accept?: string[];
  /** Tamanho máximo por arquivo em bytes (default: 50MB) */
  maxFileSize?: number;
  /** Número máximo de arquivos (default: 10) */
  maxFiles?: number;
  /** Habilitar/desabilitar */
  enabled?: boolean;
}

/**
 * Hook para gerenciar drag & drop e paste de arquivos
 *
 * @example
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null);
 * const { isDragging, files, removeFile } = useDragAndPaste({
 *   targetRef: containerRef,
 *   onFilesAdded: (files) => console.log('Files:', files),
 *   accept: ['image/*', 'video/*', 'application/pdf'],
 * });
 * ```
 */
export const useDragAndPaste = (options: UseDragAndPasteOptions) => {
  const {
    targetRef,
    onFilesAdded,
    accept = [],
    maxFileSize = 100 * 1024 * 1024, // 100MB (teto físico do send-media; limite real é por canal, validado no backend)
    maxFiles = 10,
    enabled = true,
  } = options;

  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<DragAndPasteFile[]>([]);

  /**
   * Espelha o nó de `targetRef` em estado.
   *
   * O container pode montar DEPOIS do primeiro render (o ChatBox renderiza um
   * loader enquanto `useConversation` busca a conversa) e trocar de nó a cada
   * remonta (troca de conversa). Um ref não notifica o React, então o efeito de
   * registro dos listeners não teria como saber que o elemento apareceu: rodaria
   * uma única vez, com `targetRef.current` ainda null, e drag & drop / paste
   * ficariam mortos para sempre. O efeito sem deps roda a cada render e só
   * dispara re-render quando o nó realmente muda.
   */
  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const current = targetRef.current ?? null;
    setTargetEl((prev) => (prev === current ? prev : current));
  });

  /**
   * Valida se um arquivo é aceito
   */
  const isFileAccepted = useCallback((file: File): boolean => {
    // Se não há restrições, aceita tudo
    if (accept.length === 0) return true;

    return accept.some((acceptType) => {
      if (acceptType.startsWith('.')) {
        // Aceitar por extensão — necessário para arquivos cujo MIME o browser
        // não conhece (.ofx, .ret, .rem, certificados...): file.type fica vazio
        return file.name.toLowerCase().endsWith(acceptType.toLowerCase());
      } else if (acceptType.endsWith('/*')) {
        // Aceitar por categoria: image/*, video/*, etc.
        const category = acceptType.replace('/*', '');
        return file.type.startsWith(category);
      } else {
        // Aceitar por tipo exato
        return file.type === acceptType;
      }
    });
  }, [accept]);

  /**
   * Valida tamanho do arquivo
   */
  const isFileSizeValid = useCallback((file: File): boolean => {
    return file.size <= maxFileSize;
  }, [maxFileSize]);

  /**
   * Cria preview para imagens
   */
  const createPreview = useCallback((file: File): Promise<string | undefined> => {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        resolve(undefined);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = () => {
        resolve(undefined);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  /**
   * Processa arquivos adicionados
   */
  const processFiles = useCallback(async (fileList: FileList | File[]) => {
    const filesArray = Array.from(fileList);

    // Filtrar arquivos válidos
    const validFiles = filesArray.filter((file) => {
      if (!isFileAccepted(file)) {
        console.warn(`File ${file.name} not accepted (type: ${file.type})`);
        return false;
      }

      if (!isFileSizeValid(file)) {
        console.warn(`File ${file.name} too large (${file.size} bytes)`);
        // Sem o toast o arquivo arrastado some silenciosamente — parece bug
        toast.error(
          `"${file.name}" excede o tamanho máximo de ${Math.floor(maxFileSize / (1024 * 1024))}MB`
        );
        return false;
      }

      return true;
    });

    // Limitar quantidade
    const limitedFiles = validFiles.slice(0, maxFiles - files.length);

    if (limitedFiles.length === 0) return;

    // Criar objetos com preview
    const processedFiles = await Promise.all(
      limitedFiles.map(async (file) => {
        const preview = await createPreview(file);
        return {
          file,
          preview,
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        };
      })
    );

    // Atualizar estado
    setFiles((prev) => [...prev, ...processedFiles]);

    // Callback
    onFilesAdded?.(processedFiles);
  }, [files.length, maxFiles, isFileAccepted, isFileSizeValid, createPreview, onFilesAdded]);

  /**
   * Remove arquivo da lista
   */
  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  /**
   * Limpa todos os arquivos
   */
  const clearFiles = useCallback(() => {
    setFiles([]);
  }, []);

  /**
   * Handler para drag enter
   */
  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  /**
   * Handler para drag over
   */
  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  /**
   * Handler para drag leave
   */
  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Verificar se realmente saiu do container. `relatedTarget` null = ponteiro
    // saiu da janela (ou entrou em outro documento) — sem tratar esse caso o
    // overlay trava aceso depois de arrastar para fora.
    const container = e.currentTarget as HTMLElement | null;
    if (!e.relatedTarget || !container?.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  /**
   * Handler para drop
   */
  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);

    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  }, [processFiles]);

  /**
   * Handler para paste
   */
  const handlePaste = useCallback((e: ClipboardEvent) => {
    if (e.clipboardData?.files && e.clipboardData.files.length > 0) {
      e.preventDefault();
      processFiles(e.clipboardData.files);
    }
  }, [processFiles]);

  /**
   * Registrar event listeners
   */
  useEffect(() => {
    if (!enabled || !targetEl) return;

    const target = targetEl;

    target.addEventListener('dragenter', handleDragEnter as EventListener);
    target.addEventListener('dragover', handleDragOver as EventListener);
    target.addEventListener('dragleave', handleDragLeave as EventListener);
    target.addEventListener('drop', handleDrop as EventListener);
    target.addEventListener('paste', handlePaste as EventListener);

    return () => {
      target.removeEventListener('dragenter', handleDragEnter as EventListener);
      target.removeEventListener('dragover', handleDragOver as EventListener);
      target.removeEventListener('dragleave', handleDragLeave as EventListener);
      target.removeEventListener('drop', handleDrop as EventListener);
      target.removeEventListener('paste', handlePaste as EventListener);
    };
  }, [
    enabled,
    targetEl,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handlePaste,
  ]);

  return {
    /** Se está arrastando arquivos sobre o container */
    isDragging,
    /** Lista de arquivos adicionados */
    files,
    /** Remove arquivo por ID */
    removeFile,
    /** Limpa todos os arquivos */
    clearFiles,
    /** Adiciona arquivos programaticamente */
    addFiles: processFiles,
  };
};
